import clsx from 'clsx'
import { TSizeAll } from 'moralis-ui'
import { ReactNode, useId } from 'react'
import { ToggleButton } from './ToggleButton'
import styles from './ToggleButtons.module.scss'

export type TToggleButton = {
  /**
   * each toggle button needs a unique ID
   */
  id: string
  /**
   *  required button label
   */
  label: ReactNode
}

export interface IToggleButtonsProps {
  /**
   *  className
   */
  className?: string
  /**
   * Array of items
   * ```
   * id: string
   * label: ReactNode
   * ```
   */
  items: Array<TToggleButton>
  /**
   * size
   */
  size?: TSizeAll
  /**
   * optionally apply custom CSS
   */
  style?: React.CSSProperties
  /**
   * variant
   */
  variant?: 'filled' | 'transparent' | 'outline'

  value: string

  onChange: (value: string) => void

  fullWidth?: boolean
  /**
   * id for accessibility
   */
  id?: string
}

export const ToggleButtons = ({
  items,
  size = 'md',
  variant = 'filled',
  style,
  value,
  onChange,
  fullWidth,
  id: propId,
  ...props
}: IToggleButtonsProps) => {
  const generatedId = useId()
  const id = propId || generatedId
  return (
    <div
      className={clsx(styles.wrapper, styles[variant], styles[size], {
        [styles.fullWidth]: fullWidth,
      })}
      data-testid="mui-toggle-buttons"
      role="group"
      style={{ ...style }}
      id={id}
      {...props}
    >
      {items.map((button) => (
        <ToggleButton
          key={button.id}
          wrapperId={id}
          id={button.id}
          onClick={() => onChange(button.id)}
          size={size}
          isSelected={value === button.id}
        >
          {button.label}
        </ToggleButton>
      ))}
    </div>
  )
}
